import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Plus, Save, Trash2, Edit2, Clock, 
  Lock, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

interface SessionNotesProps {
  clientId: string;
  sessionId?: string;
}

const noteTypes = [
  { value: 'session_note', label: 'Session Note' },
  { value: 'observation', label: 'Ongoing Observation' },
  { value: 'progress', label: 'Progress Note' },
  { value: 'treatment_plan', label: 'Treatment Plan Update' },
  { value: 'risk_assessment', label: 'Risk Assessment' },
];

export const SessionNotes: React.FC<SessionNotesProps> = ({ clientId, sessionId }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    note_type: 'session_note',
    is_confidential: false
  });

  const { data: notes, isLoading } = useQuery({
    queryKey: ['client-notes', clientId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('clinician_client_notes')
        .select('*')
        .eq('client_id', clientId)
        .eq('clinician_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const createNote = useMutation({
    mutationFn: async (note: typeof newNote) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('clinician_client_notes')
        .insert({
          client_id: clientId,
          clinician_id: user.id,
          ...note
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
      setIsAdding(false);
      setNewNote({ title: '', content: '', note_type: 'session_note', is_confidential: false });
      toast({
        title: 'Note saved',
        description: 'Your note has been saved successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error saving note',
        description: error.message || 'Please try again.',
        variant: 'destructive'
      });
    }
  });

  const updateNote = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; content: string; title?: string }) => {
      const { error } = await supabase
        .from('clinician_client_notes')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
      setEditingId(null);
      toast({
        title: 'Note updated',
        description: 'Your changes have been saved.',
      });
    }
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('clinician_client_notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
      toast({
        title: 'Note deleted',
        description: 'The note has been removed.',
      });
    }
  });

  const toggleExpanded = (id: string) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getNoteTypeLabel = (type: string) => {
    return noteTypes.find(t => t.value === type)?.label || type;
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Session Notes & Observations</CardTitle>
          </div>
          {!isAdding && (
            <Button size="sm" onClick={() => setIsAdding(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Add Note Form */}
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-muted/30 rounded-lg p-4 space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Note Type</Label>
                <Select
                  value={newNote.note_type}
                  onValueChange={(value) => setNewNote(prev => ({ ...prev, note_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {noteTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title (Optional)</Label>
                <Input
                  placeholder="Note title..."
                  value={newNote.title}
                  onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                placeholder="Write your note here..."
                value={newNote.content}
                onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                rows={6}
                className="resize-none"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newNote.is_confidential}
                  onCheckedChange={(checked) => setNewNote(prev => ({ ...prev, is_confidential: checked }))}
                />
                <Label className="text-sm text-muted-foreground">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Confidential (not shared with client)
                </Label>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsAdding(false)}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => createNote.mutate(newNote)}
                  disabled={!newNote.content.trim() || createNote.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Note
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Notes List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-muted/30 h-24 rounded-lg" />
            ))}
          </div>
        ) : notes?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No notes yet</p>
            <p className="text-sm">Add your first note to start tracking progress</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes?.map((note, index) => (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border border-border/50 rounded-lg overflow-hidden"
              >
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleExpanded(note.id)}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">
                      {getNoteTypeLabel(note.note_type)}
                    </Badge>
                    {note.is_confidential && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                        <Lock className="w-3 h-3 mr-1" />
                        Confidential
                      </Badge>
                    )}
                    <span className="font-medium text-foreground">
                      {note.title || 'Untitled Note'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(note.created_at), 'd MMM yyyy')}
                    </span>
                    {expandedNotes.has(note.id) ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {expandedNotes.has(note.id) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-border/50"
                  >
                    <div className="p-4 bg-muted/20">
                      {editingId === note.id ? (
                        <EditNoteForm
                          note={note}
                          onSave={(content) => updateNote.mutate({ id: note.id, content })}
                          onCancel={() => setEditingId(null)}
                          isPending={updateNote.isPending}
                        />
                      ) : (
                        <>
                          <p className="text-sm text-foreground whitespace-pre-wrap mb-4">
                            {note.content}
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingId(note.id);
                              }}
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Note</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this note? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => deleteNote.mutate(note.id)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface EditNoteFormProps {
  note: any;
  onSave: (content: string) => void;
  onCancel: () => void;
  isPending: boolean;
}

const EditNoteForm: React.FC<EditNoteFormProps> = ({ note, onSave, onCancel, isPending }) => {
  const [content, setContent] = useState(note.content);

  return (
    <div className="space-y-3">
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={6}
        className="resize-none"
      />
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          size="sm" 
          onClick={() => onSave(content)}
          disabled={!content.trim() || isPending}
        >
          <Save className="w-3 h-3 mr-1" />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default SessionNotes;
